import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import V2SEO from '../components/V2SEO';
import FAQ from '../components/FAQ';
import Tile from '../components/Showcase';
import { services, showcase, faqs, business } from '../data/realContent';

const FAQ_BY_SLUG = {
  'commercial-drone-production': [...faqs.commercial, ...faqs.general.slice(0, 3)],
  'event-drone-coverage': [...faqs.event, ...faqs.general.slice(0, 3)],
  'real-estate-drone': [...faqs.realEstate, ...faqs.general.slice(0, 3)],
  'fpv-drone-filming': [...faqs.fpv, ...faqs.general.slice(0, 3)],
  'aerial-photography': [...faqs.general],
  'construction-drone': [...faqs.commercial.slice(0, 1), ...faqs.general.slice(0, 3)],
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const examples = (service.examples || [])
    .map((id) => showcase.find((s) => s.id === id))
    .filter(Boolean);
  const photoIds = service.photoIds || [];
  const pageFaqs = FAQ_BY_SLUG[slug] || faqs.general;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    serviceType: service.name,
    description: service.blurb,
    provider: {
      '@type': 'LocalBusiness',
      name: business.name,
      telephone: business.phone,
      email: business.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: business.city,
        addressRegion: business.region,
        postalCode: business.postalCode,
        addressCountry: business.country,
      },
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Lehigh Valley' },
      { '@type': 'State', name: 'Pennsylvania' },
    ],
    audience: service.audiences && {
      '@type': 'Audience',
      audienceType: service.audiences.join(', '),
    },
  };

  return (
    <>
      <V2SEO
        title={`${service.name} in Lehigh Valley & Pennsylvania`}
        description={`${service.blurb} FAA Part 107 certified, based in ${business.city}, ${business.region}.`}
        path={`/services/${service.slug}`}
        keywords={service.keywords}
        faqs={pageFaqs}
        serviceSchema={serviceSchema}
      />

      <section className="v2-svc-hero">
        <div className="v2-container">
          <span className="v2-eyebrow">{service.short}</span>
          <h1>{service.name}</h1>
          <p className="v2-prose" style={{ marginTop: '1rem' }}>{service.blurb}</p>
          <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '1.6rem' }}>
            <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
            <Link to="/portfolio" className="v2-btn v2-btn--ghost">View Our Work</Link>
          </div>
        </div>
      </section>

      {(examples.length > 0 || photoIds.length > 0) && (
        <section className="v2-section v2-section--tight">
          <div className="v2-container">
            <div className="v2-shead" style={{ marginBottom: '2rem' }}>
              <div>
                <span className="v2-eyebrow">Portfolio</span>
                <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                  Selected Client Work
                </h2>
              </div>
            </div>

            {examples.length > 0 && (
              <div className="v2-showcase">
                {examples.slice(0, 6).map((item, i) => {
                  const sizes = ['v2-showcase__tile--wide', 'v2-showcase__tile--tall', 'v2-showcase__tile--half', 'v2-showcase__tile--half', 'v2-showcase__tile--half', 'v2-showcase__tile--half'];
                  return <Tile key={item.id} item={item} size={sizes[i]} />;
                })}
              </div>
            )}

            {photoIds.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '1rem',
                marginTop: examples.length > 0 ? '1rem' : 0,
              }}>
                {photoIds.map((p) => (
                  <div key={p} style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    aspectRatio: '4 / 3',
                    border: '1px solid var(--v2-line)',
                  }}>
                    <img src={p} alt={service.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-split">
            <div>
              <span className="v2-eyebrow">Deliverables</span>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)', margin: '0.6rem 0 1.4rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                What's Included
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {service.deliverables.map((d) => (
                  <li key={d} style={{
                    padding: '0.9rem 0',
                    borderBottom: '1px solid var(--v2-line)',
                    color: 'var(--v2-text-dim)',
                    display: 'flex',
                    gap: '0.8rem',
                  }}>
                    <span style={{ color: 'var(--v2-mint)' }}>›</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <span className="v2-eyebrow">Clients</span>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)', margin: '0.6rem 0 1.4rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                Who We Work With
              </h2>
              <div className="v2-pills">
                {service.audiences.map((a) => (
                  <span key={a} className="v2-pill">{a}</span>
                ))}
              </div>

              <div className="v2-trust" style={{ marginTop: '2rem' }}>
                <div className="v2-trust__item">
                  <strong>FAA Part 107</strong>
                  <span>Commercial drone certification, current and on file.</span>
                </div>
                <div className="v2-trust__item">
                  <strong>Insured</strong>
                  <span>Certificate of insurance available for venues and productions on request.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-shead">
            <div>
              <span className="v2-eyebrow">FAQ</span>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>{service.name} Questions</h2>
            </div>
          </div>
          <FAQ items={pageFaqs} />
        </div>
      </section>

      <section className="v2-section v2-section--tight">
        <div className="v2-container">
          <div className="v2-cta">
            <h2>Book {service.name.toLowerCase()} for your next project.</h2>
            <p>Send us the date, location, and scope of your project. We'll respond with availability and a detailed quote.</p>
            <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
              <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
              <a href={business.phoneHref} className="v2-btn v2-btn--ghost">Call {business.phone}</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
